import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { BsPencil } from 'react-icons/bs';
import { FiTrash2 } from 'react-icons/fi';
import { aclHelper } from 'helper';

import { useCreateModal } from 'partial/components/Modal';
import AsyncButton from 'modules/common/components/AsyncButton';
import ThumbIcon from 'partial/components/ThumbIcon';
import AddUpdateUser from '../modals/AddUpdateUser';
import DeleteUser from '../modals/DeleteUser';
import ResetPass from '../modals/ResetPass';
import StatusButton from '../components/StatusButton';

import { useUserById } from '../hooks';

const UserDetails = ({ users }) => {
  const { id } = useParams();
  const createModal = useCreateModal();
  const [isLoading, userDetails] = useUserById(id);

  const firstName = userDetails?.profile?.first_name || '--';
  const lastName = userDetails?.profile?.last_name || '--';
  const email = userDetails?.email || '--';

  const fullName = `${firstName} ${lastName}`;

  const handleEditUser = useCallback(() => {
    createModal({
      content: (onClose) => (
        <AddUpdateUser type="edit" onClose={onClose} prevData={userDetails} />
      ),
    });
  }, [createModal, userDetails]);

  const handleDeleteUser = useCallback(() => {
    createModal({
      content: (onClose) => (
        <DeleteUser onClose={onClose} uuid={userDetails?.uuid} users={users} />
      ),
    });
  }, [createModal, userDetails, users]);

  const handleResetPass = useCallback(() => {
    createModal({
      content: (onClose) => (
        <ResetPass onClose={onClose} uuid={userDetails?.uuid} />
      ),
    });
  }, [createModal, userDetails]);

  return (
    <div className="flex-grow pl-4">
      {isLoading ? (
        <div>
          <div className="animate-pulse flex space-x-4">
            <div className="rounded-full bg-gray-50 h-24 w-24" />
            <div className="flex-1 space-y-6 py-1">
              <div className="h-8 bg-gray-50 rounded" />
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-8 bg-gray-50 rounded col-span-2" />
                  <div className="h-8 bg-gray-50 rounded col-span-1" />
                </div>
                <div className="h-8 bg-gray-50 rounded" />
                <div className="h-8 bg-gray-50 rounded" />
                <div className="h-8 bg-gray-50 rounded" />
                <div className="h-8 bg-gray-50 rounded" />
                <div className="h-8 bg-gray-50 rounded" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="py-4 pl-6 flex justify-between  border-b border-gray-50 ">
            <figure className="flex gap-8">
              <ThumbIcon
                className="h-24 w-24 rounded-full"
                initialsClassName="text-5xl "
                alt={fullName}
              />
              <figcaption className="flex flex-col items-start pt-2 gap-1">
                <h3 className="text-2xl capitalize font-semibold text-gray-700 tracking-wider">
                  {fullName || '--'}
                </h3>
                <p className="font-light text-gray-700 tracking-wider">
                  {email || '--'}
                </p>
                <div className="px-2 py-1 text-sm font-light rounded-md text-white bg-green-500 text-center">
                  {userDetails?.role?.name || '--'}
                </div>
              </figcaption>
            </figure>
            <div className="flex items-start">
              <div className="flex flex-col xl:flex-row xl:items-center gap-2 ">
                <StatusButton userDetails={userDetails} />
                {aclHelper(
                  'user',
                  'u',
                  <AsyncButton
                    disabled={isLoading}
                    onClick={handleEditUser}
                    className="!capitalize"
                    color="regular-with-border-gray"
                  >
                    <div className="flex gap-2 items-center">
                      <BsPencil />
                      <span>edit</span>
                    </div>
                  </AsyncButton>,
                )}
                {aclHelper(
                  'user',
                  'd',
                  <AsyncButton
                    className="!capitalize"
                    color="regular-with-border-gray"
                    disabled={isLoading}
                    onClick={handleDeleteUser}
                  >
                    <div className="flex gap-2 items-center">
                      <FiTrash2 />
                      <span>delete</span>
                    </div>
                  </AsyncButton>,
                )}
              </div>
            </div>
          </div>
          <div className="py-6 pl-3">
            <h5 className="text-lg font-bold text-gray-700 mb-6">SUMMARY</h5>
            <div className="grid grid-cols-2 gap-y-4 lg:w-3/4 xl:w-1/2 ">
              <div className="text-sm tracking-wide font-light">First Name</div>
              <div className="text-sm tracking-wide font-semibold text-gray-700">
                {firstName}
              </div>
              <div className="text-sm tracking-wide font-light">
                Middle Name
              </div>
              <div className="text-sm tracking-wide font-semibold text-gray-700">
                {userDetails?.profile?.middle_name || '--'}
              </div>
              <div className="text-sm tracking-wide font-light">Last Name</div>
              <div className="text-sm tracking-wide font-semibold text-gray-700">
                {lastName}
              </div>
              <div className="text-sm tracking-wide font-light">
                Email Address
              </div>
              <div className="text-sm font-semibold text-gray-700">{email}</div>
              <div className="text-sm tracking-wide font-light">
                Mobile Number
              </div>
              <div className="text-sm tracking-wide font-semibold text-gray-700">
                {userDetails?.profile?.mobile_number || '--'}
              </div>
              {aclHelper(
                'user',
                'u',
                <>
                  <div className="text-sm tracking-wide font-light">Others</div>
                  <AsyncButton
                    className="!font-light"
                    disabled={isLoading}
                    onClick={handleResetPass}
                  >
                    reset password
                  </AsyncButton>
                </>,
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

UserDetails.propTypes = {
  users: PropTypes.instanceOf(Array).isRequired,
};

export default UserDetails;
